import { autoinject, bindable } from 'aurelia-framework';
import { NetsSalesDashboardService } from 'services/statistics-dashboard-service';
import { I18N } from 'aurelia-i18n';
import * as Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/full-screen';
import { AsyncErrorHandler } from 'lib/ui';
import { QueryFilter } from 'lib/tables/DataFilter';
import { PubSub } from 'lib/event';
import { CustomerYearlySalesReportDTO, NetDesignYearlySalesReportDTO, NetsSalesDashboard } from 'models/statistics/nets';
import { HighchartChartKind } from 'lib/highchart/types';

export interface FieldMap {
  name: 'CustomerName' | 'NetDesign';
  points: 'YearlySales';
  x: 'SalesYear';
  y: 'SalesCount';
}

type ChartView =
  | 'top-pr-customers'
  | 'top-pr-designs'
  | 'lost-offers-customers'
  | 'lost-offers-designs'
  | 'no-sales-customers'
  | 'no-sales-designs';
type ChartOptionsButton = { label: string; active: boolean; id: ChartView };

type SalesReport = CustomerYearlySalesReportDTO[] | NetDesignYearlySalesReportDTO[];

@autoinject()
export class NetsSalesWidget {
  private container!: HTMLDivElement;
  private chart?: Highcharts.Chart;

  private cutOffYears = 2;
  private dashboardData: NetsSalesDashboard;
  private tableData: SalesReport = [];

  private fieldMap: FieldMap = { name: 'CustomerName', points: 'YearlySales', x: 'SalesYear', y: 'SalesCount' };
  private chartType: HighchartChartKind = 'column';

  @bindable queryFilter: QueryFilter;
  async queryFilterChanged() {
    await this.fetchData();
    this.render();
  }

  protected fetchingData = false;
  protected chartOptionsButtons: ChartOptionsButton[] = [];
  private activeChartView: ChartView = 'top-pr-customers';

  constructor(private t: I18N, private pubsub: PubSub, private netsSalesDashboardService: NetsSalesDashboardService) {
    pubsub.sub('i18n:locale:changed', () => this.onLocaleChanged());

    this.createOptionButtons();
  }

  createOptionButtons() {
    const sales = this.t.tr('general.sales');
    // const topOrders = this.t.tr('netOffer.topOrders');
    const designType = this.t.tr('netsummary.DesignType');
    const withoutSales = this.t.tr('netoffer.withoutSales');
    const customer = this.t.tr('net.customer');
    const withoutSalesCustomer = this.t.tr('customer.withoutSales');
    const rangeFrom = this.t.tr('servicefilters.rangeFrom');
    const last = this.t.tr('general.last');
    const year = this.t.tr('general.year');

    this.chartOptionsButtons = [
      { label: `${sales} Per ${customer}`, active: true, id: 'top-pr-customers' },
      { label: `${sales} Per ${designType}`, active: true, id: 'top-pr-designs' },
      { label: `${withoutSales} Per ${customer}`, active: false, id: 'lost-offers-customers' },
      { label: `${withoutSales} Per ${designType}`, active: false, id: 'lost-offers-designs' },
      {
        label: `${withoutSalesCustomer} Per ${customer} (${rangeFrom} ${last} ${this.cutOffYears} ${year})`,
        active: false,
        id: 'no-sales-customers',
      },
      {
        label: `${withoutSalesCustomer} Per ${designType} (${rangeFrom} ${last} ${this.cutOffYears} ${year})`,
        active: false,
        id: 'no-sales-designs',
      },
    ];
  }

  async attached() {
    await this.selecteChart('top-pr-customers');
    await this.fetchData();
  }

  @AsyncErrorHandler
  async fetchData() {
    this.fetchingData = true;
    const { data } = await this.netsSalesDashboardService.getNetsSales(this.queryFilter, this.cutOffYears);
    this.dashboardData = data;
    this.fetchingData = false;

    this.assignDataForSelectedChart();
  }

  assignDataForSelectedChart() {
    if (!this.dashboardData) {
      return;
    }

    switch (this.activeChartView) {
      case 'top-pr-customers':
        this.fieldMap = { name: 'CustomerName', points: 'YearlySales', x: 'SalesYear', y: 'SalesCount' };
        this.tableData = this.dashboardData?.YearlySalesReportForCustomer;
        break;
      case 'top-pr-designs':
        this.fieldMap = { name: 'NetDesign', points: 'YearlySales', x: 'SalesYear', y: 'SalesCount' };
        this.tableData = this.dashboardData?.YearlySalesReportForNetDesign;
        break;
      case 'lost-offers-customers':
        this.fieldMap = { name: 'CustomerName', points: 'YearlySales', x: 'SalesYear', y: 'SalesCount' };
        this.tableData = this.dashboardData?.YearlyLostOffersReportForCustomer;
        break;
      case 'lost-offers-designs':
        this.fieldMap = { name: 'NetDesign', points: 'YearlySales', x: 'SalesYear', y: 'SalesCount' };
        this.tableData = this.dashboardData?.YearlyLostOffersReportForNetDesign;
        break;
      case 'no-sales-customers':
        this.fieldMap = { name: 'CustomerName', points: 'YearlySales', x: 'SalesYear', y: 'SalesCount' };
        this.tableData = this.dashboardData?.YearlySalesReportForCustomersWithoutSales;
        break;
      case 'no-sales-designs':
        this.fieldMap = { name: 'NetDesign', points: 'YearlySales', x: 'SalesYear', y: 'SalesCount' };
        this.tableData = this.dashboardData?.YearlySalesReportForNetDesignWithoutSales;
        break;
    }
  }

  async selecteChart(view: ChartView) {
    this.activeChartView = view;
    await this.fetchData();
    this.render();
  }

  private onLocaleChanged() {
    this.createOptionButtons();
    this.render();
  }

  protected detached() {
    this.destroyChart();
    this.pubsub.unsub();
  }

  private render() {
    this.destroyChart();
    if (!this.container || !this.tableData) return;

    const { name: nameKey, points: pointsKey, x: xKey, y: yKey } = this.fieldMap;
    const data = this.tableData;
    type SalesReportItem = (typeof data)[number];

    const years = Array.from(new Set(data.flatMap((item: SalesReportItem) => item[pointsKey].map((p) => +p[xKey])))).sort();

    const series: Highcharts.SeriesOptionsType[] = this.tableData
      .filter((item) => Boolean(item))
      .map((item: SalesReportItem) => {
        let label = item[nameKey] as string;
        const points = item?.[pointsKey] ?? [];

        label = label == null ? '--' : label;

        const values = years.map((y) => {
          const hit = points.find((p) => p?.[xKey] === y);
          return hit ? Number(hit?.[yKey]) : null;
        });
        return { type: this.chartType, name: label, data: values } satisfies Highcharts.SeriesOptionsType;
      });

    const BUTTON_HEIGHT = 30;
    const BUTTON_SPACING = 10;
    const TOP_CLEARANCE = BUTTON_HEIGHT + BUTTON_SPACING + 8;
    const tooltip_x = this.t.tr('general.year');
    const tooltip_y = this.t.tr('general.sales');

    const options: Highcharts.Options = {
      chart: {
        type: this.chartType,
        marginTop: TOP_CLEARANCE,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: years.map(String),
        title: { text: this.t.tr('general.year') },
      },
      yAxis: {
        title: {
          text:
            this.activeChartView == 'lost-offers-customers' || this.activeChartView == 'lost-offers-designs'
              ? this.t.tr('general.count')
              : this.t.tr('netOffer.salesCount'),
        },
        allowDecimals: false,
      },
      tooltip: {
        shared: false,
        split: false,
        useHTML: true,
        formatter: function (this: Highcharts.Point) {
          const year = this.key ?? this.category ?? this.x;
          if (this.y == null) return false;
          return `<b>${this.series?.name}</b><br/>${tooltip_x}: ${year}<br/>${tooltip_y}: ${this.y}`;
        },
      },
      plotOptions: {
        series: { connectNulls: true },
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemDistance: 30,
        itemWidth: 200,
        itemStyle: { width: 150, whiteSpace: 'normal' },
        maxHeight: 120,
      },
      series,
      navigation: {
        buttonOptions: {
          align: 'right',
          verticalAlign: 'top',
          buttonSpacing: BUTTON_SPACING,
          height: BUTTON_HEIGHT,
          y: 0,
        },
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen', 'printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF'],
          },
          toggleAll: {
            text: 'Deselect all',
            align: 'right',
            verticalAlign: 'top',
            titleKey: 'toggleSeries',
            onclick: function (this: Highcharts.Chart) {
              // Treat undefined `visible` as true
              const allVisible = this.series.every((s) => s.visible !== false);
              this.series.forEach((s) => s.setVisible(!allVisible, false));
              this.redraw();
              const newLabel = allVisible ? 'Select all' : 'Deselect all';
              this.update({ exporting: { buttons: { toggleAll: { text: newLabel } } } }, false);
              this.redraw();
            },
          },
        },
      },
    };
    this.chart = Highcharts.chart(this.container, options);
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }
}
